import React, { useState } from 'react';
import styled from 'styled-components';
import {
  colorThemeGenerator,
  ButtonPrimary,
  IconChevronDown,
  IconChevronRight,
  Type,
  TypeList,
  TypeListItem,
} from '@spotify-internal/encore-creator-web';
import {
  white,
  spacer16,
  spacer24,
  screenMdMin,
  aquamarine,
  midnight,
  screenSmMax,
} from '@spotify-internal/encore-foundation';
import { pageConstraintStyles, PAGE_GUTTER } from './styles';
import { TextWithDesktopLineBreak } from '../Shared/TextWithDesktopLineBreak';
import { useIntl, FormattedMessage, defineMessage } from 'react-intl';
import { PODCASTERS_ZENDESK_LINK } from '../../lib/constants';
import { useLocalePreferenceContext } from '../../hooks/useLocalePreference';
import { login } from '../../lib/Request';

export function Faqs() {
  const [activeFaqIndex, setActiveFaqIndex] = useState(-1);
  const intl = useIntl();
  const localePreference = useLocalePreferenceContext();

  const faqs = [
    {
      question: intl.formatMessage(
        defineMessage({
          id: 'landing-page.faq.how-podcast-on-spotify',
          defaultMessage: 'How do I get my podcast on Spotify?',
        }),
      ),
      answer: (
        <>
          <FaqAnswer variant={Type.body1} weight={Type.bold}>
            <FormattedMessage id="landing-page.faq.add-a-podcast" defaultMessage="Add a podcast:" />
          </FaqAnswer>
          <TypeList as="ol">
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.click-get-started-add-a-podcast"
                defaultMessage="Click <link>Get Started</link>."
                values={{
                  link: chunks => (
                    <button
                      onClick={login}
                      style={{ color: white, fontWeight: 700, textDecoration: 'underline' }}
                    >
                      {chunks}
                    </button>
                  ),
                }}
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.paste-in-the-link"
                defaultMessage="Paste in the link to your podcast's RSS feed."
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.add-podcast-info"
                defaultMessage="Add podcast info like the category, language, and country."
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.review-the-details"
                defaultMessage="Review the details and submit."
              />
            </FaqAnswer>
          </TypeList>
          <FaqAnswer variant={Type.body1} weight={Type.bold}>
            <FormattedMessage
              id="landing-page.faq.claim-a-podcast"
              defaultMessage="Claim a podcast:"
            />
          </FaqAnswer>
          <TypeList as="ol" style={{ paddingBottom: 0 }}>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.click-get-started-claim-a-podcast"
                defaultMessage="Click <link>Get Started</link>."
                values={{
                  link: chunks => (
                    <button
                      onClick={login}
                      style={{ color: white, fontWeight: 700, textDecoration: 'underline' }}
                    >
                      {chunks}
                    </button>
                  ),
                }}
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.paste-and-submit"
                defaultMessage="Paste and submit a link to your podcast's RSS feed."
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.check-the-email"
                defaultMessage="Check the email that’s linked to your RSS feed. You’ll receive an automatic
              verification email with an 8-digit code."
              />
            </FaqAnswer>
            <FaqAnswer as={TypeListItem}>
              <FormattedMessage
                id="landing-page.faq.paste-your-8-digit"
                defaultMessage="Paste your 8-digit code into the box provided and submit."
              />
            </FaqAnswer>
          </TypeList>
        </>
      ),
    },
    {
      question: intl.formatMessage(
        defineMessage({
          id: 'landing-page.faq.how-often-podcast-updated',
          defaultMessage: 'How often is my podcast data updated?',
        }),
      ),
      answer: (
        <FaqAnswer as="p">
          <FormattedMessage
            id="landing-page.faq.your-podcasts-data"
            defaultMessage="Your podcast data’s updated daily. When you add new podcasts or episodes to Spotify, their
          data will show up one day later."
          />
        </FaqAnswer>
      ),
    },
    {
      question: intl.formatMessage(
        defineMessage({
          id: 'landing-page.faq.who-are-listeners',
          defaultMessage: 'Who are my listeners and followers?',
        }),
      ),
      answer: (
        <FaqAnswer as="p">
          <FormattedMessage
            id="landing-page.faq.listeners-are-unique"
            defaultMessage="Listeners are unique listeners who started an episode in your catalog. Followers are
          listeners who hit Follow on your podcast on Spotify. They automatically receive new
          episodes of your podcast."
          />
        </FaqAnswer>
      ),
    },
    {
      question: intl.formatMessage(
        defineMessage({
          id: 'landing-page.faq.how-are-starts',
          defaultMessage: 'How are starts and streams counted?',
        }),
      ),
      answer: (
        <>
          <FaqAnswer as="p" style={{ marginBottom: spacer16 }}>
            <FormattedMessage
              id="landing-page.faq.starts-measure-any"
              defaultMessage="Starts measure any listener who clicks on a podcast episode. There’s no minimum time
            limit to be counted as a start on Spotify."
            />
          </FaqAnswer>
          <FaqAnswer as="p">
            <FormattedMessage
              id="landing-page.faq.streams-are-counted"
              defaultMessage="Streams are counted in Spotify for Podcasters when a podcast’s streamed for over 60
            seconds."
            />
          </FaqAnswer>
        </>
      ),
    },
    {
      question: intl.formatMessage(
        defineMessage({
          id: 'landing-page.faq.can-i-export',
          defaultMessage: 'Can I export a report with my data?',
        }),
      ),
      answer: (
        <FaqAnswer as="p">
          <FormattedMessage
            id="landing-page.faq.yep-you-can"
            defaultMessage="Yep! You can download a .CSV file with your data anywhere you see an arrow next to the
          date range."
          />
        </FaqAnswer>
      ),
    },
  ];

  const toggleFaq = index => {
    if (activeFaqIndex === index) {
      setActiveFaqIndex(-1);
    } else {
      setActiveFaqIndex(index);
    }
  };

  return (
    <FaqContainer>
      <FaqContent>
        <FaqHeader>
          <FormattedMessage
            id="landing-page.faq.frequently-asked"
            defaultMessage="Frequently <break>asked questions</break>"
            values={{
              break: chunks => (
                <TextWithDesktopLineBreak as="span">{chunks}</TextWithDesktopLineBreak>
              ),
            }}
          />
        </FaqHeader>
        <div>
          {faqs.map((faq, i) => (
            <div key={faq.question} style={{ marginBottom: '28px' }}>
              <div
                onClick={() => toggleFaq(i)}
                style={{ display: 'flex' }}
                data-testid={`landing-page--faq-question-${i}`}
              >
                {activeFaqIndex === i ? (
                  <IconChevronDown iconSize={16} />
                ) : (
                  <IconChevronRight iconSize={16} />
                )}
                <FaqQuestion>{faq.question}</FaqQuestion>
              </div>
              <FaqAnswerContainer
                active={activeFaqIndex === i}
                data-testid={`landing-page--faq-answer-${i}`}
              >
                {faq.answer}
              </FaqAnswerContainer>
            </div>
          ))}
          <ButtonPrimary
            href={PODCASTERS_ZENDESK_LINK + localePreference.languageAndRegionCode}
            target="_blank"
            theme={{
              ...colorThemeGenerator({
                primaryColor: aquamarine,
                primaryColorText: midnight,
                primaryColorFocusText: midnight,
                primaryColorActiveText: midnight,
              }),
            }}
            style={{ marginTop: spacer16 }}
          >
            <FormattedMessage id="landing-page.faq.see-more-faq" defaultMessage="See More Faq" />
          </ButtonPrimary>
        </div>
      </FaqContent>
    </FaqContainer>
  );
}

const FaqContainer = styled.div`
  background-image: url(/images/landing_page/bg-08-faq.svg);
  background-size: 2000px;
  background-position: top;
  background-repeat: no-repeat;
  background-color: ${midnight};
  @media (max-width: ${screenSmMax}) {
    background-image: none;
  }
`;

const FaqContent = styled.div`
  ${pageConstraintStyles}

  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${spacer16};
  padding: 50px 16px;

  @media (min-width: ${screenMdMin}) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 30px;
    padding: 120px 95px 178px ${PAGE_GUTTER};
  }
`;

const FaqHeader = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading1,
})`
  font-size: 28px;
  color: ${aquamarine};

  @media (min-width: ${screenMdMin}) {
    font-size: 50px;
  }
`;

const FaqQuestion = styled(Type).attrs({
  forwardedAs: 'p',
})`
  font-size: 18px;
  margin-left: 12px;
  padding: 0;

  @media (min-width: ${screenMdMin}) {
    font-size: 22px;
  }
`;

const FaqAnswerContainer = styled.div<{
  active: boolean;
}>`
  margin: ${spacer24} 0 0 30px;
  display: ${props => (props.active ? 'block' : 'none')};
`;

const FaqAnswer = styled(Type)`
  font-size: 16px;
  line-height: 28px;
  padding-bottom: 0;
`;
